import { CANCEL } from 'redux-saga';
import ROUTES from '@giro-pdv/constants/routes.constant';

export class PinpadError extends Error {
  constructor(
    message: string,
    public kind = 'agent',
    public details: Record<string, any> = {}
  ) {
    super(message);
    this.name = 'PinpadError';
  }
}

export const REQUEST_TIMEOUT = 15000;

// A timed-out POST may have reached the agent. Never retry it here.
export function requestAgent(
  endpoint: string,
  method = 'POST',
  data?: Record<string, any>,
  expected?: number[] | 'transaction',
  timeout = REQUEST_TIMEOUT
) {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  let interrupt: (error: Error) => void;
  const stopped = new Promise<never>((_, reject) => {
    interrupt = reject;
    timer = setTimeout(() => {
      controller.abort();
      reject(
        new PinpadError(
          'O agente do pinpad não respondeu a tempo.',
          'timeout',
          { endpoint }
        )
      );
    }, timeout);
  });
  const request = async () => {
    const options: RequestInit = {
      method,
      cache: 'no-store',
      signal: controller.signal,
    };
    if (data) {
      options.headers = {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      };
      const body = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null)
          body.append(key, String(value));
      });
      options.body = body;
    }
    const response = await fetch(
      `${ROUTES.EXTERNAL.PINPAD}${endpoint}`,
      options
    );
    if (!response.ok) {
      throw new PinpadError(
        'Não foi possível acessar o agente do pinpad.',
        'http',
        { endpoint, status: response.status }
      );
    }
    const result = await response.json();
    if (!result || result.serviceStatus !== 0) {
      throw new PinpadError(
        result?.serviceMessage || 'Resposta inválida do agente do pinpad.',
        'agent',
        {
          endpoint,
          serviceStatus: result?.serviceStatus,
        }
      );
    }
    if (
      data?.sessionId &&
      result.sessionId &&
      data.sessionId !== result.sessionId
    ) {
      throw new PinpadError(
        'A sessão do pinpad mudou durante a operação.',
        'session',
        { endpoint }
      );
    }
    if (
      expected &&
      (!Number.isInteger(result.clisitefStatus) ||
        (expected !== 'transaction' &&
          !expected.includes(result.clisitefStatus)))
    ) {
      throw new PinpadError(
        endpoint === '/pinpad/isPresent'
          ? 'Pinpad não encontrado. Verifique o cabo e a porta configurada.'
          : `Falha na comunicação com o pinpad (código ${result.clisitefStatus}).`,
        endpoint === '/pinpad/isPresent' ? 'absent' : 'clisitef',
        { endpoint, clisitefStatus: result.clisitefStatus }
      );
    }
    return result;
  };
  const promise = Promise.race([request(), stopped])
    .catch((error) => {
      if (error instanceof PinpadError) throw error;
      throw new PinpadError(
        'Não foi possível comunicar com o agente do pinpad. Verifique se ele está em execução.',
        'network',
        { endpoint }
      );
    })
    .finally(() => clearTimeout(timer));
  promise[CANCEL] = () => {
    controller.abort();
    interrupt(
      new PinpadError('Comunicação com o pinpad interrompida.', 'cancelled', {
        endpoint,
      })
    );
  };
  return promise;
}
